import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Base64 URL-safe helpers (browser side)
const b64urlToBuf = (s) => {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};

const bufToB64url = (buf) => {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const passkeySupported = () =>
  typeof window !== 'undefined' &&
  typeof window.PublicKeyCredential === 'function' &&
  typeof navigator.credentials?.create === 'function';

export const isPlatformAuthenticatorAvailable = async () => {
  try {
    if (typeof window?.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
};

// Register a passkey for the currently authenticated customer
export const registerPasskey = async (authHeaders) => {
  const challengeResp = await axios.post(`${API}/auth/passkey/register-challenge`, {}, { headers: authHeaders });
  const { options, challenge_b64 } = challengeResp.data;

  // Decode base64url fields the server already provides as b64
  const publicKey = {
    ...options,
    challenge: b64urlToBuf(options.challenge),
    user: { ...options.user, id: b64urlToBuf(options.user.id) },
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({ ...c, id: b64urlToBuf(c.id) })),
  };

  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error('Cancelled');

  const credential = {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: bufToB64url(cred.response.attestationObject),
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
  await axios.post(`${API}/auth/passkey/register-verify`, { credential, challenge_b64 }, { headers: authHeaders });
  return { ok: true };
};

// Login with passkey — returns { access_token, email }
export const loginWithPasskey = async (email) => {
  const challengeResp = await axios.post(`${API}/auth/passkey/login-challenge`, { email });
  const { options, challenge_b64 } = challengeResp.data;

  const publicKey = {
    ...options,
    challenge: b64urlToBuf(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((c) => ({ ...c, id: b64urlToBuf(c.id) })),
  };

  const cred = await navigator.credentials.get({ publicKey });
  if (!cred) throw new Error('Cancelled');

  const credential = {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      authenticatorData: bufToB64url(cred.response.authenticatorData),
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
      signature: bufToB64url(cred.response.signature),
      userHandle: cred.response.userHandle ? bufToB64url(cred.response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
  const verifyResp = await axios.post(`${API}/auth/passkey/login-verify`, { email, credential, challenge_b64 });
  return verifyResp.data;
};

export const checkPasskey = async (email) => {
  try {
    const { data } = await axios.post(`${API}/auth/passkey/check`, { email });
    return data.has_passkey === true;
  } catch {
    return false;
  }
};


// ---------- Admin passkeys ----------
export const registerAdminPasskey = async (authHeaders) => {
  const challengeResp = await axios.post(`${API}/admin/passkey/register-challenge`, {}, { headers: authHeaders });
  const { options, challenge_b64 } = challengeResp.data;
  const publicKey = {
    ...options,
    challenge: b64urlToBuf(options.challenge),
    user: { ...options.user, id: b64urlToBuf(options.user.id) },
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({ ...c, id: b64urlToBuf(c.id) })),
  };
  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error('Cancelled');
  const credential = {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: bufToB64url(cred.response.attestationObject),
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
  await axios.post(`${API}/admin/passkey/register-verify`, { credential, challenge_b64 }, { headers: authHeaders });
  return { ok: true };
};

export const loginAdminWithPasskey = async (email) => {
  const challengeResp = await axios.post(`${API}/auth/admin/passkey/login-challenge`, { email });
  const { options, challenge_b64 } = challengeResp.data;
  const publicKey = {
    ...options,
    challenge: b64urlToBuf(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((c) => ({ ...c, id: b64urlToBuf(c.id) })),
  };
  const cred = await navigator.credentials.get({ publicKey });
  if (!cred) throw new Error('Cancelled');
  const credential = {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      authenticatorData: bufToB64url(cred.response.authenticatorData),
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
      signature: bufToB64url(cred.response.signature),
      userHandle: cred.response.userHandle ? bufToB64url(cred.response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
  const { data } = await axios.post(`${API}/auth/admin/passkey/login-verify`, { email, credential, challenge_b64 });
  return data;
};

export const checkAdminPasskey = async (email) => {
  try {
    const { data } = await axios.post(`${API}/auth/admin/passkey/check`, { email });
    return data.has_passkey === true;
  } catch {
    return false;
  }
};


// ---------- Driver passkeys ----------
export const registerDriverPasskey = async (authHeaders) => {
  const challengeResp = await axios.post(`${API}/driver/passkey/register-challenge`, {}, { headers: authHeaders });
  const { options, challenge_b64 } = challengeResp.data;
  const publicKey = {
    ...options,
    challenge: b64urlToBuf(options.challenge),
    user: { ...options.user, id: b64urlToBuf(options.user.id) },
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({ ...c, id: b64urlToBuf(c.id) })),
  };
  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error('Cancelled');
  const credential = {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: bufToB64url(cred.response.attestationObject),
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
  await axios.post(`${API}/driver/passkey/register-verify`, { credential, challenge_b64 }, { headers: authHeaders });
  return { ok: true };
};

export const loginDriverWithPasskey = async (phoneOrEmail) => {
  const challengeResp = await axios.post(`${API}/auth/driver/passkey/login-challenge`, { phone_or_email: phoneOrEmail });
  const { options, challenge_b64, driver_id } = challengeResp.data;
  const publicKey = {
    ...options,
    challenge: b64urlToBuf(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((c) => ({ ...c, id: b64urlToBuf(c.id) })),
  };
  const cred = await navigator.credentials.get({ publicKey });
  if (!cred) throw new Error('Cancelled');
  const credential = {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      authenticatorData: bufToB64url(cred.response.authenticatorData),
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
      signature: bufToB64url(cred.response.signature),
      userHandle: cred.response.userHandle ? bufToB64url(cred.response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
  const { data } = await axios.post(`${API}/auth/driver/passkey/login-verify`, { driver_id, credential, challenge_b64 });
  return data;
};

export const checkDriverPasskey = async (phoneOrEmail) => {
  try {
    const { data } = await axios.post(`${API}/auth/driver/passkey/check`, { phone_or_email: phoneOrEmail });
    return data.has_passkey === true;
  } catch {
    return false;
  }
};
